import { KeyboardEvent, useId } from 'react';
import './ridTabs.css';

type RidTabItem = {
  id: string;
  label: string;
};

type RidTabsProps = {
  tabs: RidTabItem[];
  activeTabId: string;
  onTabChange: (tabId: string) => void;
  ariaLabel: string;
  className?: string;
  idBase?: string;
};

export default function RidTabs({
  tabs,
  activeTabId,
  onTabChange,
  ariaLabel,
  className,
  idBase,
}: RidTabsProps) {
  const generatedIdBase = useId();
  const tabsIdBase = idBase || generatedIdBase;

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!tabs.length) {
      return;
    }

    const moveFocusTo = (nextIndex: number) => {
      const nextTab = tabs[nextIndex];
      if (!nextTab) {
        return;
      }
      onTabChange(nextTab.id);
      requestAnimationFrame(() => {
        const nextButton = document.getElementById(`${tabsIdBase}-tab-${nextTab.id}`);
        if (nextButton instanceof HTMLButtonElement) {
          nextButton.focus();
        }
      });
    };

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveFocusTo((index + 1) % tabs.length);
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveFocusTo((index - 1 + tabs.length) % tabs.length);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      moveFocusTo(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      moveFocusTo(tabs.length - 1);
    }
  };

  const tabsClasses = ['rid-tabs', className].filter(Boolean).join(' ');

  return (
    <nav className={tabsClasses} aria-label={ariaLabel} role="tablist" data-testid="landing-tabs" style={{ '--rid-tab-count': tabs.length } as React.CSSProperties}>
      {tabs.map((tab, index) => {
        const isActive = activeTabId === tab.id;
        return (
          <button
            key={tab.id}
            id={`${tabsIdBase}-tab-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`${tabsIdBase}-panel-${tab.id}`}
            data-testid={`landing-tab-${tab.id}`}
            data-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            className={`rid-tabs__tab ${isActive ? 'rid-tabs__tab--active' : 'rid-tabs__tab--inactive'}`}
            onClick={() => onTabChange(tab.id)}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
          >
            {tab.label}
          </button>
        );
      })}
    </nav>
  );
}
